import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "phaseCard",
    "triggerButton",
    "statusIndicator",
    "storyCount",
    "progressBar"
  ]
  
  static values = {
    projectId: Number,
    phaseId: String,
    triggerUrl: String,
    retryUrl: String,
    statusUrl: String
  }
  
  connect() {
    console.log("Feature phases controller connected")
    this.updatePhaseStatuses()
  }
  
  async triggerPhase(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const phaseName = button.dataset.phaseName
    const phaseDisplay = button.dataset.phaseDisplay || phaseName
    
    if (!confirm(`Generate stories for ${phaseDisplay} phase?`)) {
      return
    }
    
    button.disabled = true
    button.textContent = "Triggering..."
    
    try {
      const response = await post(button.dataset.triggerUrl, {
        responseKind: "json"
      })
      
      if (response.ok) {
        const data = await response.json
        this.showNotification(`${phaseDisplay} phase generation started`, "success")
        
        // Update UI
        this.markPhaseInProgress(phaseName)
        button.textContent = "In Progress..."
        
        // Start polling for this phase
        this.startPhasePolling(phaseName)
      } else {
        const error = await response.json
        throw new Error(error.message)
      }
    } catch (error) {
      console.error("Error triggering phase:", error)
      this.showNotification(error.message || "Failed to trigger phase", "error")
      button.disabled = false
      button.textContent = "Trigger"
    }
  }
  
  async retryPhase(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const phaseName = button.dataset.phaseName
    
    if (!confirm(`Retry ${phaseName} phase generation?`)) {
      return
    }
    
    button.disabled = true
    button.textContent = "Retrying..."
    
    try {
      const response = await post(button.dataset.retryUrl, {
        responseKind: "json"
      })
      
      if (response.ok) {
        const data = await response.json
        this.showNotification("Phase retry started", "success")
        
        // Update UI
        this.markPhaseInProgress(phaseName)
        button.remove() // Remove retry button
        
        // Start polling
        this.startPhasePolling(phaseName)
      } else {
        throw new Error("Failed to retry phase")
      }
    } catch (error) {
      console.error("Error retrying phase:", error)
      this.showNotification("Failed to retry phase", "error")
      button.disabled = false
      button.textContent = "Retry"
    }
  }
  
  markPhaseInProgress(phaseName) {
    const phaseCard = this.findPhaseCard(phaseName)
    if (!phaseCard) return
    
    // Update status indicator
    const statusIndicator = phaseCard.querySelector('[data-status-indicator]')
    if (statusIndicator) {
      statusIndicator.className = "badge badge-warning"
      statusIndicator.textContent = "In Progress"
    }
    
    // Show progress spinner
    const spinner = phaseCard.querySelector('[data-spinner]')
    if (spinner) {
      spinner.classList.remove('hidden')
    }
    
    // Add pulsing animation
    phaseCard.classList.add('animate-pulse')
  }
  
  markPhaseComplete(phaseName, storyCount) {
    const phaseCard = this.findPhaseCard(phaseName)
    if (!phaseCard) return
    
    // Update status indicator
    const statusIndicator = phaseCard.querySelector('[data-status-indicator]')
    if (statusIndicator) {
      statusIndicator.className = "badge badge-success"
      statusIndicator.textContent = "Completed"
    }
    
    // Update story count
    const countElement = phaseCard.querySelector('[data-story-count]')
    if (countElement) {
      countElement.textContent = `${storyCount} stories`
    }
    
    // Hide spinner
    const spinner = phaseCard.querySelector('[data-spinner]')
    if (spinner) {
      spinner.classList.add('hidden')
    }
    
    // Remove animation
    phaseCard.classList.remove('animate-pulse')
    
    // Update trigger button
    const triggerButton = phaseCard.querySelector('[data-trigger-button]')
    if (triggerButton) {
      triggerButton.textContent = "Completed"
      triggerButton.disabled = true
      triggerButton.classList.add('btn-success')
    }
  }
  
  markPhaseFailed(phaseName, error) {
    const phaseCard = this.findPhaseCard(phaseName)
    if (!phaseCard) return
    
    // Update status indicator
    const statusIndicator = phaseCard.querySelector('[data-status-indicator]')
    if (statusIndicator) {
      statusIndicator.className = "badge badge-error"
      statusIndicator.textContent = "Failed"
    }
    
    // Hide spinner
    const spinner = phaseCard.querySelector('[data-spinner]')
    if (spinner) {
      spinner.classList.add('hidden')
    }
    
    // Remove animation
    phaseCard.classList.remove('animate-pulse')
    
    // Show retry button
    const triggerButton = phaseCard.querySelector('[data-trigger-button]')
    if (triggerButton) {
      triggerButton.textContent = "Retry"
      triggerButton.disabled = false
      triggerButton.classList.add('btn-error')
      triggerButton.dataset.action = "click->feature-phases#retryPhase"
    }
    
    // Show error message
    if (error) {
      const errorElement = phaseCard.querySelector('[data-error-message]')
      if (errorElement) {
        errorElement.textContent = error
        errorElement.classList.remove('hidden')
      }
    }
  }
  
  findPhaseCard(phaseName) {
    return document.querySelector(`[data-phase-name="${phaseName}"]`)
  }
  
  startPhasePolling(phaseName) {
    // Poll every 2 seconds for this specific phase
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${this.statusUrlValue}?phase=${phaseName}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          credentials: 'same-origin'
        })
        
        if (response.ok) {
          const data = await response.json()
          
          if (data.status === 'completed') {
            this.markPhaseComplete(phaseName, data.story_count)
            clearInterval(pollInterval)
          } else if (data.status === 'failed') {
            this.markPhaseFailed(phaseName, data.error)
            clearInterval(pollInterval)
          }
          
          // Update progress if available
          if (data.progress) {
            this.updatePhaseProgress(phaseName, data.progress)
          }
        }
      } catch (error) {
        console.error("Error polling phase status:", error)
      }
    }, 2000)
    
    // Store interval for cleanup
    this.pollIntervals = this.pollIntervals || {}
    this.pollIntervals[phaseName] = pollInterval
  }
  
  updatePhaseProgress(phaseName, progress) {
    const phaseCard = this.findPhaseCard(phaseName)
    if (!phaseCard) return
    
    const progressBar = phaseCard.querySelector('[data-progress-bar]')
    if (progressBar) {
      progressBar.style.width = `${progress}%`
      progressBar.textContent = `${progress}%`
      progressBar.parentElement.classList.remove('hidden')
    }
  }
  
  updatePhaseStatuses() {
    // Update all phase cards based on their current status
    this.phaseCardTargets.forEach(card => {
      const status = card.dataset.phaseStatus
      const phaseName = card.dataset.phaseName
      
      switch(status) {
        case 'not_started':
          // Enable trigger button if dependencies met
          const canTrigger = card.dataset.canTrigger === 'true'
          const button = card.querySelector('[data-trigger-button]')
          if (button) {
            button.disabled = !canTrigger
            if (!canTrigger) {
              button.title = "Complete required phases first"
            }
          }
          break
        case 'in_progress':
          this.markPhaseInProgress(phaseName)
          this.startPhasePolling(phaseName)
          break
        case 'completed':
          const storyCount = card.dataset.storyCount || 0
          this.markPhaseComplete(phaseName, storyCount)
          break
        case 'failed':
          this.markPhaseFailed(phaseName)
          break
      }
    })
  }
  
  showNotification(message, type = 'info') {
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed bottom-4 right-4 z-50 max-w-md`
    toast.innerHTML = `
      <div>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.remove()
    }, 5000)
  }
  
  disconnect() {
    // Clean up any polling intervals
    if (this.pollIntervals) {
      Object.values(this.pollIntervals).forEach(interval => {
        clearInterval(interval)
      })
    }
  }
}